import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ── Types ─────────────────────────────────────────────

export interface PortfolioSummary {
  totalLoans: number;
  totalPrincipal: number;
  activeLoans: number;
  activePrincipal: number;
  delinquentLoans: number;
  delinquentPrincipal: number;
  delinquencyRate: number;
  paidOffLoans: number;
  paidOffPrincipal: number;
  pipelineLoans: number;
  pipelinePrincipal: number;
  avgPrincipal: number;
  avgRate: number;
  weightedAvgRate: number;
  avgTermMonths: number;
}

export interface StatusDistItem {
  status: string;
  count: number;
  totalPrincipal: number;
}

export interface TypeBreakdownItem {
  loanType: string;
  count: number;
  totalPrincipal: number;
  avgRate: number;
}

export interface DelinquencyBucket {
  count: number;
  amount: number;
}

export interface DelinquencyReport {
  totalAtRisk: number;
  totalAtRiskAmount: number;
  buckets: Record<string, DelinquencyBucket>;
  loans: any[];
}

export interface PipelineItem {
  status: string;
  count: number;
  totalPrincipal: number;
  avgPrincipal: number;
}

export interface TopBorrower {
  id: string;
  name: string;
  email: string;
  loanCount: number;
  totalPrincipal: number;
  activeLoans: number;
}

export interface MonthlyOriginationItem {
  month: string;
  count: number;
  amount: number;
}

// ── Hooks ─────────────────────────────────────────────

export function usePortfolioSummary() {
  return useQuery<PortfolioSummary>({
    queryKey: ['reports', 'portfolio-summary'],
    queryFn: () => api.get('/reports/portfolio-summary').then(r => r.data),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useStatusDistribution() {
  return useQuery<StatusDistItem[]>({
    queryKey: ['reports', 'status-distribution'],
    queryFn: () => api.get('/reports/status-distribution').then(r => r.data),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useTypeBreakdown() {
  return useQuery<TypeBreakdownItem[]>({
    queryKey: ['reports', 'type-breakdown'],
    queryFn: () => api.get('/reports/type-breakdown').then(r => r.data),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useDelinquencyReport() {
  return useQuery<DelinquencyReport>({
    queryKey: ['reports', 'delinquency'],
    queryFn: () => api.get('/reports/delinquency').then(r => r.data),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function usePipelineReport() {
  return useQuery<PipelineItem[]>({
    queryKey: ['reports', 'pipeline'],
    queryFn: () => api.get('/reports/pipeline').then(r => r.data),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useTopBorrowers(limit = 10) {
  return useQuery<TopBorrower[]>({
    queryKey: ['reports', 'top-borrowers', limit],
    queryFn: () => api.get(`/reports/top-borrowers?limit=${limit}`).then(r => r.data),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useMonthlyOrigination(months = 12) {
  return useQuery<MonthlyOriginationItem[]>({
    queryKey: ['reports', 'monthly-origination', months],
    queryFn: () => api.get(`/reports/monthly-origination?months=${months}`).then(r => r.data),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}
